import { c as _c } from "react/compiler-runtime";
import Card from '@precis-digital/kurama/Card';
import Chip from '@precis-digital/kurama/Chip';
import Grid from '@precis-digital/kurama/Grid';
import Tooltip from '@precis-digital/kurama/Tooltip';
import Typography from '@precis-digital/kurama/Typography';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import type { TFunction } from 'shared/translations';
import { useMemo, type ReactElement } from 'react';
import { AvatarWithLabel } from 'shared/components/AvatarWithLabel';
import { ASSETS_BASE_URL } from 'shared/constants';
import { useCurrentClient } from 'shared/context/ClientContext';
import { useQueryMembers } from 'src/workspace/api';
import { getRecommendationTypes, STATUS } from '../constants';
import { type GroupedRecommendationItem } from '../types';
import { mapMessageWithParams } from '../utils';
import AssigneeTooltip from './AssigneeTooltip';
import Severity, { SeverityTooltip } from './Severity';
import TitleWithAvatarsAffectedEntities from 'shared/components/TitleWithAvatarsAffectedEntities';
import { useAccountsAvatarsAffected, useEntitiesAffected } from '../useRecommendationAffected';
import ThemedAvatar from 'shared/components/Themed/Avatar';
const IconComponent = t0 => {
  const $ = _c(2);
  const {
    Icon: IconProp
  } = t0;
  let t1;
  if ($[0] !== IconProp) {
    t1 = <StyledIconWrapper><IconProp /></StyledIconWrapper>;
    $[0] = IconProp;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface RecommendationCardProps {
  onClick: () => void;
  data: GroupedRecommendationItem;
  t: TFunction<'recommendation'>;
}
const RecommendationCard = t0 => {
  const $ = _c(64);
  const {
    onClick,
    data,
    t
  } = t0;
  const theme = useTheme();
  const recommendationTypes = getRecommendationTypes(theme);
  const accountsAvatarsAffected = useAccountsAvatarsAffected(data);
  const entitiesAffected = useEntitiesAffected(data);
  const {
    currentClient
  } = useCurrentClient();
  const {
    data: members
  } = useQueryMembers(currentClient?.id);
  let t1;
  if ($[0] !== data.assignee || $[1] !== members) {
    t1 = members?.find(member => member.id === data.assignee);
    $[0] = data.assignee;
    $[1] = members;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const assigneeMember = t1;
  let t2;
  bb0: {
    if (data.status === "ignoredUntil") {
      t2 = STATUS.IGNORED;
      break bb0;
    }
    let t3;
    if ($[3] !== data.status) {
      t3 = Object.values(STATUS).find(option => option.value === data.status) ?? STATUS.UNRESOLVED;
      $[3] = data.status;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = t3;
  }
  const statusOption = t2;
  const T0 = StyledRecommendationCard;
  const t3 = data.messageId;
  const T1 = Grid;
  const t4 = "flex";
  const t5 = "row";
  const t6 = "space-between";
  const t7 = "8px";
  const T2 = Tooltip;
  const t8 = "multiline";
  const t9 = t(recommendationTypes[data.category].type);
  const t10 = t(recommendationTypes[data.category].description);
  let t11;
  if ($[5] !== data.Icon) {
    t11 = <IconComponent Icon={data.Icon} />;
    $[5] = data.Icon;
    $[6] = t11;
  } else {
    t11 = $[6];
  }
  let t12;
  if ($[7] !== data.backgroundColor || $[8] !== t11) {
    t12 = <StyledCategoryGrid $backgroundColor={data.backgroundColor}>{t11}</StyledCategoryGrid>;
    $[7] = data.backgroundColor;
    $[8] = t11;
    $[9] = t12;
  } else {
    t12 = $[9];
  }
  let t13;
  if ($[10] !== T2 || $[11] !== t10 || $[12] !== t12 || $[13] !== t9) {
    t13 = <T2 kind={t8} title={t9} body={t10}>{t12}</T2>;
    $[10] = T2;
    $[11] = t10;
    $[12] = t12;
    $[13] = t9;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== data.severity || $[16] !== onClick) {
    t14 = <Severity onClick={onClick} type={data.severity} />;
    $[15] = data.severity;
    $[16] = onClick;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== data.severity || $[19] !== t14) {
    t15 = <SeverityTooltip type={data.severity}>{t14}</SeverityTooltip>;
    $[18] = data.severity;
    $[19] = t14;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  let t16;
  if ($[21] !== T1 || $[22] !== t13 || $[23] !== t15) {
    t16 = <T1 display={t4} flexDirection={t5} justifyContent={t6} gap={t7}>{t13}{t15}</T1>;
    $[21] = T1;
    $[22] = t13;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  const t17 = data.name;
  let t18;
  if ($[25] !== t) {
    t18 = t("affected");
    $[25] = t;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] !== accountsAvatarsAffected || $[28] !== data.name || $[29] !== entitiesAffected || $[30] !== t18) {
    t19 = <TitleWithAvatarsAffectedEntities title={t17} titleVariant="h4" affectedLabel={t18} {...accountsAvatarsAffected} {...entitiesAffected} />;
    $[27] = accountsAvatarsAffected;
    $[28] = data.name;
    $[29] = entitiesAffected;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== data.paramsValues || $[33] !== data.recommendation) {
    t20 = mapMessageWithParams(data.recommendation, data.paramsValues);
    $[32] = data.paramsValues;
    $[33] = data.recommendation;
    $[34] = t20;
  } else {
    t20 = $[34];
  }
  let t21;
  if ($[35] !== t20) {
    t21 = <Grid marginY="4px"><StyledTypography2LineClamp variant="body3">{t20}</StyledTypography2LineClamp></Grid>;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  const t22 = data.assignee != null && assigneeMember != null ? assigneeMember.name : "Unassigned";
  let t23;
  if ($[37] !== assigneeMember || $[38] !== data.assignee) {
    t23 = data.assignee != null && assigneeMember != null ? <AvatarWithLabel user={assigneeMember} size="small" /> : <ThemedAvatar kind="image" imageUrl={`${ASSETS_BASE_URL}/unassigned_icon.svg`} size="small" />;
    $[37] = assigneeMember;
    $[38] = data.assignee;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== onClick || $[41] !== t22 || $[42] !== t23) {
    t24 = <StyledAssigneeChip label={t22} avatar={t23} onClick={onClick} />;
    $[40] = onClick;
    $[41] = t22;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== assigneeMember || $[45] !== t24) {
    t25 = <AssigneeTooltip assigneeMember={assigneeMember}>{t24}</AssigneeTooltip>;
    $[44] = assigneeMember;
    $[45] = t24;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== onClick || $[48] !== statusOption.icon) {
    t26 = <Chip avatar={statusOption.icon} onClick={onClick} />;
    $[47] = onClick;
    $[48] = statusOption.icon;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== statusOption.label || $[51] !== t26) {
    t27 = <Tooltip kind="singleline" title={statusOption.label}>{t26}</Tooltip>;
    $[50] = statusOption.label;
    $[51] = t26;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t25 || $[54] !== t27) {
    t28 = <Grid marginTop="4px" display="flex" justifyContent="space-between" width="100%" flexWrap="wrap" gap="4px">{t25}{t27}</Grid>;
    $[53] = t25;
    $[54] = t27;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  let t29;
  if ($[56] !== T0 || $[57] !== data.messageId || $[58] !== onClick || $[59] !== t16 || $[60] !== t19 || $[61] !== t21 || $[62] !== t28) {
    t29 = <T0 key={t3} onClick={onClick}>{t16}{t19}{t21}{t28}</T0>;
    $[56] = T0;
    $[57] = data.messageId;
    $[58] = onClick;
    $[59] = t16;
    $[60] = t19;
    $[61] = t21;
    $[62] = t28;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  return t29;
};
const StyledRecommendationCard = styled(Card)(({
  theme
}) => ({
  marginTop: '0',
  borderRadius: '24px',
  backgroundColor: theme.tokens.background.main.default,
  display: 'flex',
  gap: '4px',
  alignItems: 'start',
  flexDirection: 'column',
  width: '100%'
}));
const StyledTypography2LineClamp = styled(Typography)(({
  theme
}) => ({
  display: '-webkit-box',
  WebkitLineClamp: '2',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
}));
const StyledCategoryGrid = styled(Grid)<{
  $backgroundColor: string;
}>(({
  $backgroundColor: backgroundColor,
  theme
}) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100%',
  backgroundColor,
  borderRadius: theme.borderRadius.small
}));
const StyledIconWrapper = styled(Grid)(({
  theme
}) => ({
  display: 'flex',
  padding: '4px 8px 4px 8px',
  svg: {
    color: theme.tokens.foreground.main.staticWhite
  }
}));
const StyledAssigneeChip = styled(Chip)(({
  theme
}) => ({
  '& > .kurama-chip-label': {
    maxWidth: '15em',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }
}));
export default RecommendationCard;